import { useCallback, useEffect, useRef, useState } from "react";
import styles from "../../../pages/superadmin/Receipts.module.css";
import { useAuth } from "../../context/Auth";
import Button from "../Dealers/Button";
import SlidingFilter2 from "../Dealers/SlidingFilter2";
import TopTabBar from "../Tanolet/TopTabBar";
import { TemplateNotFound } from "../../icons/Icon2";
import NoDataFound from "../NoDataFound/NoDataFound";
import Skeleton from "../Skeleton/Skeleton";
import VerticalDropDown from "../FormComponents/VerticalDropDown";
import TextElement from "../FormComponents/TextElement";
import ModalWrapper from "../layoutWrapper/ModalWrapper";
import ManagerModal from "../Manager/ManagerModal";
import Pagination2 from "../FormComponents/Pagination2";
import fetchData from "../../../APIsControll/apiControll";
import CheckBox from "../FormComponents/CheckBox";
import { useToast } from "../Toaster/Toaster";
import InputTypedate from "../FormComponents/InputTypedate";

const BulkReception = () => {
  const { mode, managerId } = useAuth();
  const { show } = useToast();

  const tableRef = useRef(null);

  const [daysTab, setDaysTab] = useState("today");
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [managers, setManagers] = useState([]);
  const [selectedManager, setSelectedManager] = useState({});
  const [selectedRows, setSelectedRows] = useState([]);
  const [allData, setAllData] = useState([]);
  const [loading, setloading] = useState(false);
  const [isFetchingAll, setIsFetchingAll] = useState(false);
  const [allItemsCache, setAllItemsCache] = useState([]);
  const [limit, setLimit] = useState(1);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const getAllData = useCallback(async () => {
    if (daysTab === "period" && (startDate === "" || endDate === "")) return;

    try {
      const apiData = {
        managerId: managerId,
        sortByManagerId: selectedManager.value,
        day: daysTab,
        startDate: startDate,
        endDate: endDate,
      };
      setloading(true);
      const data = await fetchData(
        `receipt/allSettelCash?page=${page}&limit=${limit}`,
        apiData
      );
      setloading(false);
      setAllData(data.data);
      setManagers(
        data.paymentManagers.map((r) => ({
          label: r.name,
          value: r.managerId,
        }))
      );
      setPages(data.pages);
    } catch (error) {
      setloading(false);
      console.error(error);
    }
  }, [
    daysTab,
    endDate,
    limit,
    managerId,
    page,
    selectedManager.value,
    startDate,
  ]);

  useEffect(() => {
    if (managerId && limit !== 1) {
      getAllData();
      setAllItemsCache([]);
    }
  }, [getAllData, daysTab, limit, managerId, page, selectedManager.value]);

  const fetchAllPages = async () => {
    if (allItemsCache.length > 0) return allItemsCache;

    try {
      setIsFetchingAll(true);
      const apiData = {
        managerId: managerId,
        sortByManagerId: selectedManager.value,
        day: daysTab,
        startDate: "",
        endDate: "",
      };

      let allItems = [];
      let currentPage = 1;
      let totalPages = 1;
      while (currentPage <= totalPages) {
        const data = await fetchData(
          `receipt/allSettelCash?page=${currentPage}&limit=${limit}`,
          apiData
        );

        allItems = [...allItems, ...data.data];
        totalPages = data.pages;
        currentPage++;

        if (currentPage <= totalPages) {
          await new Promise((resolve) => setTimeout(resolve, 100));
        }
      }

      setAllItemsCache(allItems);
      return allItems;
    } catch (error) {
      console.error(error);
      show("Failed to fetch all data", "error");
      return [];
    } finally {
      setIsFetchingAll(false);
    }
  };

  useEffect(() => {
    const calculateLimit = () => {
      if (!tableRef.current) return;

      const containerHeight = tableRef?.current?.clientHeight;

      const width = window.innerWidth;

      let rowHeight = 45;

      if (width < 1600) {
        rowHeight = 37;
      }

      const visibleRows = Math.floor(containerHeight / rowHeight);
      const newLimit = visibleRows > 0 ? visibleRows : 20;

      setLimit((prevLimit) => (prevLimit !== newLimit ? newLimit : prevLimit));
    };

    calculateLimit();
    window.addEventListener("resize", calculateLimit);
    return () => window.removeEventListener("resize", calculateLimit);
  }, []);

  const settleCollection = async () => {
    const apiData = {
      managerId: managerId,
      selectedReceipt: selectedRows.map(({ paymentAmount, ...rest }) => rest),
    };

    try {
      const data = await fetchData("receipt/settelCash", apiData);

      if (data.status === "success") {
        show(data.msg, "success");
        getAllData();
        setSelectedRows([]);
        setAllItemsCache([]);
      }

      if (data.status === "failed") {
        show(data.msg, "error");
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleCheck = (item) => {
    setSelectedRows((prev) => {
      const exists = prev.some((row) => row.receiptId === item.receiptId);
      if (exists) {
        return prev.filter((row) => row.receiptId !== item.receiptId);
      } else {
        return [
          ...prev,
          {
            receiptId: item.receiptId,
            orderId: item.orderId,
            payMId: item.payMId,
            paymentAmount: item.paymentAmount,
          },
        ];
      }
    });
  };

  const handleCheckAll = async () => {
    if (
      selectedRows.length > 0 &&
      allItemsCache.length > 0 &&
      selectedRows.length === allItemsCache.length
    ) {
      setSelectedRows([]);
      return;
    }

    try {
      const allItems = await fetchAllPages();
      setSelectedRows(
        allItems.map((r) => ({
          receiptId: r.receiptId,
          orderId: r.orderId,
          payMId: r.payMId,
          paymentAmount: r.paymentAmount,
        }))
      );
    } catch (error) {
      console.error("Error selecting all items:", error);
      show("Failed to select all items", "error");
    }
  };

  const daysTabChange = (label) => {
    const selectedFilter = daysData.find((item) => item.label === label);
    const newDayTab = selectedFilter?.value || "today";
    setDaysTab(newDayTab);
    setAllItemsCache([]);
    setSelectedRows([]);
  };

  const totalAmount = selectedRows.reduce(
    (sum, row) => sum + row.paymentAmount,
    0
  );

  const formatDate = (dateString) => {
    if (!dateString) return "Invalid Date";

    const [day, month, year] = dateString.split("-");
    const formattedDate = `${year}-${month}-${day}`;

    const date = new Date(`${formattedDate}T00:00:00`);

    if (isNaN(date)) {
      console.error("Invalid date:", dateString);
      return "Invalid Date";
    }

    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const today = new Date();
  const currentYear = today.getFullYear();

  const financialYearStart = new Date(
    today.getMonth() < 3 ? currentYear - 1 : currentYear,
    3,
    1
  );
  const minDate = financialYearStart.toISOString().split("T")[0];

  const daysData = [
    { value: "today", label: "Today" },
    { value: "yesterday", label: "Yesterday" },
    { value: "month", label: "This Month" },
    { value: "period", label: "Period" },
  ];

  const receiptHeader = [
    "check",
    "Date & Time",
    "Receipt/Order ID",
    "Name & City & ID",
    "Created By",
    "Order Manager",
    "Collector",
    "Delivery Partner",
    "Amount",
    "Method",
    "Last Action",
  ];

  const isAllSelected = allData.every((item) =>
    selectedRows.some((row) => row.receiptId === item.receiptId)
  );

  return (
    <div
      className={styles.bulk_update}
      style={mode ? { backgroundColor: "#2C2E33", borderColor: "#2C2E33" } : {}}
    >
      <ModalWrapper
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        noBtn
      >
        <ManagerModal
          heading={"Settle Payment"}
          centerHeading={"Are you sure you want to Settle?"}
          subHeading={"To confirm! type Settle in the text field."}
          targetWord={"settle"}
          handleSubmit={settleCollection}
          handleCloseModal={() => setIsModalOpen(false)}
          focus={isModalOpen}
        />
      </ModalWrapper>

      <div className={styles.bu_top}>
        <div className={styles.receipts_top_left}>
          <TopTabBar text={"Settle Payment"} />
        </div>

        <div className={styles.receipts_right}>
          <Pagination2
            mode={mode}
            currentPage={page}
            setCurrentPage={setPage}
            totalPages={pages}
          />
        </div>
      </div>

      <div className={styles.bu_mid}>
        <div className={styles.horizontal_dd}>
          <p style={{ color: mode && "#FFFFFF" }}>Select Manager</p>
          <div>
            <VerticalDropDown
              dropDownText={"Select"}
              options={managers}
              value={
                Object.keys(selectedManager)?.length === 0
                  ? { value: 0, label: "All" }
                  : selectedManager
              }
              onchange={(v) => {
                setSelectedManager(v);
                setAllItemsCache([]);
                setSelectedRows([]);
              }}
              height={"37px"}
              fontSize={"12px"}
            />
          </div>
        </div>

        <div className={styles.bu_mid_right}>
          <TextElement
            label={"Total Amount:"}
            labelColor={mode ? "#fff" : "#646B88"}
            value={totalAmount}
            valueColor={mode ? "#fff" : "#646B88"}
            fontWeight={"400"}
          />{" "}
          <div>
            <SlidingFilter2
              itemList={daysData.map((item) => item.label)}
              setFilter={daysTabChange}
              selectedItem={
                daysData.find((item) => item.value === daysTab)?.label
              }
            />
          </div>
          {daysTab === "period" && (
            <>
              <div>
                <InputTypedate
                  text={"From"}
                  value={startDate}
                  max={endDate}
                  onChange={(e) => {
                    setStartDate(e.target.value);
                    if (daysTab !== "period") {
                      setDaysTab("");
                    }
                    // setStatus("");
                  }}
                  min={minDate}
                />
              </div>
              {startDate !== "" && (
                <div>
                  <InputTypedate
                    text={"To"}
                    value={endDate}
                    min={startDate}
                    onChange={(e) => {
                      setEndDate(e.target.value);
                      if (daysTab !== "period") {
                        setDaysTab("");
                      }
                      // setStatus("");
                    }}
                  />
                </div>
              )}
            </>
          )}
          {selectedRows?.length !== 0 && (
            <div>
              <Button
                buttonText={"Settle"}
                buttonBgColor={"#4164E3"}
                buttonTextColor={"#ffffff"}
                buttonBorder={"none"}
                buttonClick={() => setIsModalOpen(true)}
                disabled={isFetchingAll}
              />
            </div>
          )}
        </div>
      </div>

      <ul className={styles.receipts_data} ref={tableRef}>
        <li
          className={`${styles.bu_data_header} ${
            mode ? styles.bu_data_header_dark : ""
          }`}
        >
          {receiptHeader.map((h, index) =>
            h === "check" ? (
              <span className={styles.entry_2} key={1}>
                <CheckBox
                  checked={isAllSelected && selectedRows.length !== 0}
                  handleChange={handleCheckAll}
                  disabled={isFetchingAll}
                />
              </span>
            ) : (
              <div className={styles.entry_2} key={`${h}-${index}`}>
                {h}
              </div>
            )
          )}
        </li>
        <div className={styles.receipts_data_list}>
          {loading || isFetchingAll ? (
            <div>
              <Skeleton
                lines={15}
                thickness={5}
                additionalStyles={{ marginBottom: "0px", marginTop: "0px" }}
              />
            </div>
          ) : (
            <>
              {!loading && allData?.length === 0 ? (
                <div className={styles.no_data}>
                  <NoDataFound
                    icon={<TemplateNotFound />}
                    textTitle={`No Data Found`}
                  />
                </div>
              ) : (
                <>
                  {allData?.map((d, index) => (
                    <div key={index}>
                      <li
                        className={`${styles.bu_data_row} ${
                          mode ? styles.bu_data_row_dark : ""
                        }`}
                      >
                        <div className={styles.entry_2}>
                          <CheckBox
                            checked={selectedRows.some(
                              (row) => row.receiptId === d.receiptId
                            )}
                            handleChange={() => handleCheck(d)}
                          />
                        </div>
                        <div className={styles.entry_2}>
                          {d.receiptTime}, {formatDate(d.receiptDate)}
                        </div>
                        <div className={styles.entry_2}>
                          {d.receiptId}/{d.orderId}{" "}
                        </div>
                        <div className={styles.entry_2}>
                          {d.storeName}, {d.city}, {d.dealerId}{" "}
                        </div>
                        <div className={styles.entry_2}>{d.createdBy}</div>
                        <div className={styles.entry_2}>{d.ordermanager}</div>
                        <div className={styles.entry_2}>
                          {d.paymentCollecter}
                        </div>
                        <div className={styles.entry_2}>{d.paymentMethod}</div>
                        <div className={styles.entry_2}>{d.paymentAmount}</div>
                        <div className={styles.entry_2}>{d.partnerName}</div>
                        <div className={styles.entry_2} title={d.lastAction}>
                          {d.lastAction}
                        </div>
                      </li>
                    </div>
                  ))}
                </>
              )}
            </>
          )}
        </div>
      </ul>
    </div>
  );
};

export default BulkReception;
